import React from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Phone, MapPin, IndianRupee, Clock, MessageCircle } from "lucide-react";

export default function DoctorCard({ doctor, compact = false }) {
  const d = doctor;
  const specialtyLabel = (d.specialty_slug || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const areaLabel = (d.area_slug || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const wa = (d.whatsapp || d.phone || "").replace(/[^\d]/g, "");
  const waMsg = encodeURIComponent(`Hi Dr. ${d.name}, I found you on LocalDoctor.in and would like to book an appointment.`);
  return (
    <article
      className="group relative rounded-xl border border-border bg-card p-5 card-hover flex flex-col gap-4"
      data-testid={`doctor-card-${d.slug}`}
    >
      <div className="flex items-start gap-4">
        <div className="h-16 w-16 rounded-lg bg-muted overflow-hidden shrink-0 ring-1 ring-border">
          {d.photo_url ? (
            <img src={d.photo_url} alt={d.name} loading="lazy" className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full grid place-items-center text-muted-foreground font-semibold">
              {d.name?.split(" ").slice(0, 2).map((p) => p[0]).join("")}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-[17px] font-semibold leading-tight">
              <Link to={`/doctor/${d.slug}`} className="hover:text-primary">{d.name}</Link>
            </h3>
            {d.verified && (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--ld-jade-fg))] bg-accent px-2 py-0.5 rounded-full">
                <BadgeCheck size={13} /> Verified
              </span>
            )}
            {d.is_demo && (
              <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                Demo Profile
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">{specialtyLabel} · {d.experience_years}+ yrs exp</p>
          <p className="text-sm text-muted-foreground">{d.qualification}</p>
        </div>
      </div>

      {!compact && (
        <div className="grid grid-cols-2 gap-2 text-[13px] text-foreground/80">
          <p className="flex items-start gap-1.5"><MapPin size={14} className="mt-0.5 text-muted-foreground shrink-0" /><span className="truncate">{d.clinic_name}, {areaLabel}</span></p>
          <p className="flex items-start gap-1.5 tabular"><IndianRupee size={14} className="mt-0.5 text-muted-foreground shrink-0" />{d.consultation_fee} consultation</p>
          <p className="flex items-start gap-1.5 col-span-2"><Clock size={14} className="mt-0.5 text-muted-foreground shrink-0" />{d.timings || "By appointment"}</p>
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <Link to={`/doctor/${d.slug}`} className="btn-primary flex-1 py-2.5 text-[13px]" data-testid={`view-profile-${d.slug}`}>
          View Profile
        </Link>
        <a
          href={`tel:${d.phone}`}
          className="btn-secondary py-2.5 px-3 text-[13px]"
          aria-label={`Call ${d.name}`}
          data-testid={`call-${d.slug}`}
        >
          <Phone size={16} />
        </a>
        {wa && (
          <a
            href={`https://wa.me/${wa}?text=${waMsg}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary py-2.5 px-3 text-[13px] text-[hsl(var(--ld-jade-fg))]"
            aria-label={`WhatsApp ${d.name}`}
            data-testid={`whatsapp-${d.slug}`}
          >
            <MessageCircle size={16} />
          </a>
        )}
      </div>
    </article>
  );
}
